import {ChangeDetectorRef, Component, DestroyRef, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {FeedPost, Post} from '../../_types/content';
import {AuthService} from '../../_services/auth.service';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {CreatePostComponent} from '../../shared/create-post/create-post.component';
import {EMPTY, Observable, switchMap, take} from 'rxjs';
import {ConfirmationDialogComponent} from '../../shared/confirmation-dialog/confirmation-dialog.component';
import {ConfirmationDialogData} from '../../_types/shared';
import {ContentService} from '../../_services/content.service';
import {CommentsComponent} from '../../shared/comments/comments.component';
import {SharedStateService} from '../../_services/shared-state.service';
import {PollViewComponent} from '../../shared/poll-view/poll-view.component';
import {CommunitiesService} from '../../_services/communities.service';
import {Community, UserAccessLevel} from '../../_types/communities';
import {LocalStorageService} from '../../_services/local-storage.service';
import {SnackbarService} from '../../_services/snackbar.service';
import {ActivatedRoute, Router} from '@angular/router';
import {animate, keyframes, state, style, transition, trigger} from '@angular/animations';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {ThemeService} from "../../_services/theme.service";
import {Heatmap, Poll} from '../../_types/polls';
import {HeatmapVoteResultsComponent} from '../../shared/heatmap-vote-results/heatmap-vote-results.component';
import {User} from '../../_types/user';
import {PollsService} from "../../_services/polls.service";

@Component({
    selector: 'app-post-card',
    templateUrl: './post-card.component.html',
    styleUrls: ['./post-card.component.scss', '../feed.component.scss'],
    animations: [
        trigger('doubleBlink', [
            state('blink', style({})),
            transition('void => blink', [
                animate('300ms', style({backgroundColor: 'var(--theme-accent-200)'})),
                animate('300ms', style({})),
                animate('300ms', style({backgroundColor: 'var(--theme-accent-200)'})),
                animate('300ms', style({}))
            ])
        ]),
        trigger('likeAnimation', [
            state('inactive', style({
                transform: 'scale(1)'
            })),
            state('active', style({
                transform: 'scale(1)'
            })),
            transition('inactive => active', [
                animate('300ms ease-in-out', keyframes([
                    style({transform: 'scale(1)', offset: 0}),
                    style({transform: 'scale(1.4)', offset: 0.5}),
                    style({transform: 'scale(1)', offset: 1.0})
                ]))
            ]),
            transition('active => inactive', [
                animate('100ms ease-out')
            ])
        ]),
    ]
})
export class PostCardComponent implements OnInit {
    @ViewChild(PollViewComponent, {static: false}) pollView?: PollViewComponent;

    @Input({required: true}) post!: Post | FeedPost;
    @Input() showPinnedBadge: boolean = false;
    @Input() showOpenInFeed: boolean = false;
    @Input() hidePoll: boolean = false;
    @Input() hideMedia: boolean = false;
    @Input() smallPoll: boolean = false;
    @Input() hideActions: boolean = false;
    @Input() reducedContent: boolean = false;
    @Input() hideAttachments: boolean = false;

    @Output() action: EventEmitter<'edit' | 'delete'> = new EventEmitter<'edit' | 'delete'>();

    isModalActive$: Observable<boolean> = this.sharedStateService.isDialogOpen();
    blinkState = 'void';

    urlOrigin: string = location.origin
    community: Community | null = null

    isSuperuser: boolean = false;
    userInfoMenuOpen: boolean = false;
    pollId: number | undefined;
    selectedCommunity: Community | null = null;
    isRequiredScrollToComment: boolean = false;
    user: User | null = null;

    constructor(
        private readonly authService: AuthService,
        private readonly contentService: ContentService,
        private readonly dialog: MatDialog,
        private readonly cdr: ChangeDetectorRef,
        private readonly sharedStateService: SharedStateService,
        private readonly communitiesService: CommunitiesService,
        private readonly localStorageService: LocalStorageService,
        protected readonly snackbarService: SnackbarService,
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private readonly destroyRef: DestroyRef,
        protected themeService: ThemeService,
        public changeDetection: ChangeDetectorRef,
        private readonly pollsService: PollsService,
    ) {
        this.contentService.getCurrentDashboardCommunity()
            .pipe(takeUntilDestroyed())
            .subscribe(community => {
                this.community = community;
            });
    }

    hasCommunityRole(roles: UserAccessLevel[]): boolean {
        return this.contentService.userHasCommunityRole(roles);
    }

    ngOnInit(): void {
        this.isSuperuser = this.authService.isSuperUser();

        if (this.post?.polls?.[0]) {
            const poll: number | Poll = this.post.polls?.[0];
            this.pollId = typeof poll === 'number' ? poll : poll?.id;
        }

        this.selectedCommunity = this.localStorageService.getItem<Community | null>(
            'lastSelectedCommunity',
            null
        );

        this.isRequiredScrollToComment = this.route.snapshot.queryParamMap.has('comment');

        this.user = this.authService.getUser();
    }

    editPost(post: Post | FeedPost): void {
        const poll: Poll | undefined = this.pollView?.pollData;
        this.changeDetection.detach();
        const dialogRef: MatDialogRef<CreatePostComponent> = this.dialog.open(CreatePostComponent, {
            panelClass: 'mediumWindow',
            data: {
                post,
                poll
            },
        });
        dialogRef.componentInstance.removedPollOrHeatmap.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((result): void => {
                if (result === 'poll') {
                    this.post = {
                        ...this.post,
                        polls: []
                    };
                } else if (result === 'heatmap') {
                    this.post = {
                        ...this.post,
                        heatmap_surveys: []
                    };
                }
            })
        dialogRef.afterClosed().pipe(take(1)).subscribe((result: boolean): void => {
            this.changeDetection.reattach();
            if (!result) return;
            this.action.emit('edit');
            this.changeDetection.reattach();
        });
    }

    toggleEmoji(post: FeedPost | Post): void {
        this.contentService.updatePost(post.id, {emoji_enabled: !post?.emoji_enabled}, true)
            .pipe(take(1))
            .subscribe((): void => {
                const status: 'enabled' | 'disabled' = post?.emoji_enabled ? 'enabled' : 'disabled';
                this.snackbarService.openSuccessSnackBar(`Emoji has been successfully ${status}.`);
                this.action.emit('edit')
            });
    }

    markModerated(post: FeedPost | Post): void {
        this.contentService.updatePost(post.id, {moderated: true}, true)
            .pipe(take(1))
            .subscribe((): void => {
                this.snackbarService.openSuccessSnackBar(`Post has been successfully marked as moderated.`);
                this.action.emit('edit')
            });
    }

    deletePost(postId: number): void {
        const dialogRef: MatDialogRef<ConfirmationDialogComponent> = this.dialog.open<ConfirmationDialogComponent, ConfirmationDialogData>(ConfirmationDialogComponent, {
            panelClass: 'mediumWindow',
            data: {
                showSubmitBtn: true,
                showCancelBtn: true,
                message: 'Are you sure you want to delete this post? <br>(<i>Please note</i>: all comments will also be deleted)',
                title: 'Delete post',
                showHeader: true
            }
        });
        dialogRef.afterClosed()
            .pipe(
                switchMap((result: boolean): Observable<void> => {
                    return result ? this.contentService.deletePost(postId) : EMPTY
                }),
                take(1)
            )
            .subscribe((): void => {
                this.snackbarService.openSuccessSnackBar('Post has been successfully deleted.');
                this.action.emit('edit');
            });
    }

    viewComments(post: Post | FeedPost, commentForScroll?: number): void {
        const poll: Poll | undefined = this.pollView?.pollData;
        this.contentService.getPostComments(post.id).pipe(take(1)).subscribe((comments): void => {
            this.changeDetection.detach();
            const dialogRef: MatDialogRef<CommentsComponent> = this.dialog.open(CommentsComponent, {
                data: {
                    comments: comments,
                    post,
                    poll,
                    commentForScroll
                },
                autoFocus: false,
                panelClass: ['largeWindow', 'w-100', 'comments-modal'],
            });
            dialogRef.afterClosed().pipe(take(1)).subscribe((): void => {
                this.changeDetection.reattach();
                if (poll && this.pollId) {
                    this.pollsService.getPollById(this.pollId).pipe(take(1)).subscribe((res): void => {
                        this.pollView!.updateData(res);
                    });
                }
            });
        });
    }

    findPostInFeed(post: Post | FeedPost): void {
        if (this.selectedCommunity?.id === post.community) {
            this.router.navigate(['/feed'], {queryParams: {post: post.id}});
        } else {
            this.communitiesService.getUserCommunities()
                .pipe(
                    take(1)
                )
                .subscribe(res => {
                    const community: Community | undefined = res.map(userCommunity => userCommunity.community).find(userCommunity => userCommunity.id === post.community);
                    if (!community) {
                        this.snackbarService.openErrorSnackBar('Error redirecting to post');
                        return
                    }
                    this.contentService.setCurrentDashboardCommunity(community);
                    this.router.navigate(['/feed'], {queryParams: {post: post.id}});
                })
        }
    }

    startBlinkAnimation(): void {
        this.blinkState = 'blink';
        this.contentService.reportAction('post-open', 'post', this.post?.id, this.selectedCommunity?.id).subscribe(r => {
        })
        if (this.isRequiredScrollToComment) {
            this.viewComments(this.post, +this.route.snapshot.queryParamMap.get('comment')!);
        }
        //  TODO remove param so refresh doesnt trigger it again
    }

    hasVideo(): boolean {
        let hasVideo: boolean = false;
        for (let attachment of this.post.attachments) {
            if (attachment.attachment_type == 'video') {
                hasVideo = true;
            }
        }
        return hasVideo
    }

    showHeatmapResults(heatmap_surveys: Heatmap[] | undefined): void {
        this.dialog.open(HeatmapVoteResultsComponent, {
            panelClass: 'heatmap-results-dialog',
            data: {
                heatmap: heatmap_surveys
            }
        })
    }

    likePost(post: Post | FeedPost): void {
        const obs$ = !post.is_liked ? this.contentService.likePost(post.id) : this.contentService.unlikePost(post.id);
        this.post.is_liked = !this.post.is_liked;
        if (this.post.is_disliked) {
            this.post.is_disliked = false;
        }
        obs$.pipe(take(1)).subscribe((res): void => {
            this.post.likes_count = res.data.likes_count;
            if (Object.hasOwn(res.data, 'dislikes_count')) {
                this.post.dislikes_count = res.data.dislikes_count;
            } else if (this.post.dislikes_count > 0) {
                this.post.dislikes_count--
            }
            this.cdr.detectChanges();
        });
    }

    dislikePost(post: Post | FeedPost): void {
        const obs$ = !post.is_disliked ? this.contentService.dislikePost(this.post.id) : this.contentService.unDislikePost(this.post.id);
        this.post.is_disliked = !this.post.is_disliked;
        if (this.post.is_liked) {
            this.post.is_liked = false;
        }
        obs$.pipe(take(1)).subscribe((res): void => {
            if (Object.hasOwn(res.data, 'likes_count')) {
                this.post.likes_count = res.data.likes_count;
            } else if (this.post.likes_count > 0) {
                this.post.likes_count--
            }

            if (Object.hasOwn(res.data, 'dislikes_count')) {
                this.post.dislikes_count = res.data.dislikes_count;
            } else {
                this.post.dislikes_count++
            }
            this.cdr.detectChanges();
        });
    }

}
